import type { Species } from "../lib/types";
import { hasRealCommonName } from "../../shared/species";
import { tierToRank } from "../lib/rarity";
import { openPhoto } from "./PhotoViewer";
import {
  Chip,
  CheckIcon,
  ChevIcon,
  PlusIcon,
  RarityPill,
  SpeciesRow,
} from "../ui";

// Lagoon SpeciesCard — the on-list row for Home. Composes SpeciesRow
// (abyss card + PhotoFrame + name + latin) with rarity + status chips
// on top and a trailing log/undo control. State semantics unchanged:
//   "new"      — not in the lifelist
//   "lifelist" — seen before but not on the active trip
//   "checked"  — logged on the active trip / this session

export type SpeciesCardState = "new" | "lifelist" | "checked";

export function SpeciesCard({
  species,
  state,
  onToggle,
  onRemove,
  onCompare,
  onInfo,
  priority = false,
}: {
  species: Species;
  state: SpeciesCardState;
  onToggle: () => void;
  onRemove?: () => void;
  onCompare?: () => void;
  onInfo?: () => void;
  priority?: boolean;
}) {
  const checked = state === "checked";
  const inLifelist = state === "lifelist";
  const hasCommon = hasRealCommonName(
    species.common_name,
    species.scientific_name,
  );
  const displayName = hasCommon ? species.common_name : species.scientific_name;

  const chips = (
    <>
      <RarityPill rank={tierToRank(species.observation_count)} />
      {inLifelist && <Chip>In lifelist</Chip>}
      {checked && <Chip>On trip</Chip>}
    </>
  );

  const markCls = checked
    ? "bg-aqua-deep text-paper"
    : inLifelist
      ? "bg-paper-ink/[0.08] text-paper-ink ring-1 ring-inset ring-paper-ink/40"
      : "bg-paper-ink/[0.08] text-paper-ink/70";

  const trailing = (
    <div className="flex items-center gap-1.5">
      {onCompare && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCompare();
          }}
          aria-label={`Compare ${displayName} with similar species`}
          title="Could be confused with"
          className="flex h-11 w-11 items-center justify-center rounded-full text-paper-ink/60 ring-1 ring-inset ring-paper-ink/15 transition hover:bg-paper-ink/[0.08] hover:text-paper-ink"
        >
          {/* two overlapping circles — "compare" glyph without shipping a new icon */}
          <span aria-hidden className="relative block h-4 w-5">
            <span className="absolute left-0 top-0 h-3 w-3 rounded-full border-2 border-current" />
            <span className="absolute left-2 top-1 h-3 w-3 rounded-full border-2 border-current" />
          </span>
        </button>
      )}
      {onInfo && (
        <button
          type="button"
          onClick={onInfo}
          aria-label={`Show details for ${displayName}`}
          className="flex h-11 w-11 items-center justify-center rounded-full text-paper-ink/60 ring-1 ring-inset ring-paper-ink/15 transition hover:bg-paper-ink/[0.08] hover:text-paper-ink"
        >
          <ChevIcon className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={checked ? (onRemove ?? onToggle) : onToggle}
        aria-pressed={checked}
        aria-label={
          checked ? `Remove ${displayName}` : `Log ${displayName}`
        }
        className={`flex h-11 w-11 items-center justify-center rounded-full transition ${markCls}`}
      >
        {checked ? (
          <CheckIcon className="h-4 w-4" />
        ) : (
          <PlusIcon className="h-4 w-4" />
        )}
      </button>
    </div>
  );

  // Row-tap fallback: if the caller only wired onCompare (no onInfo),
  // taps still open compare. When both handlers exist, prefer the
  // details drawer for row-tap since the Compare button is now visible.
  return (
    <SpeciesRow
      name={displayName}
      latin={species.scientific_name}
      photo={species.photo_url}
      chips={chips}
      trailing={trailing}
      priority={priority}
      onClick={onInfo ?? onCompare}
      onPhotoClick={
        species.photo_url
          ? () =>
              openPhoto({
                url: species.photo_url,
                common_name: species.common_name,
                scientific_name: species.scientific_name,
              })
          : undefined
      }
    />
  );
}
